// apps/ecommerce/src/components/ai-chat/session-selector.tsx
import { useEffect, useState, useRef } from "react";
import { useStore } from "~/stores/store-provider";
import type { ShopifyChatSession } from "~/lib/network/shopify-chat";

interface SessionSelectorProps {
  connectionId: string;
  onSessionSelect: (sessionId: string) => void;
}

export function SessionSelector({ connectionId, onSessionSelect }: SessionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const deleteModalRef = useRef<HTMLDialogElement>(null);

  const {
    shopifyChatSessions,
    currentShopifySessionId,
    fetchShopifyChatSessions,
    selectShopifyChatSession,
    renameShopifyChatSession,
    deleteShopifyChatSession,
  } = useStore();

  // Fetch sessions on mount
  useEffect(() => {
    fetchShopifyChatSessions(connectionId);
  }, [connectionId, fetchShopifyChatSessions]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const currentSession = shopifyChatSessions.find(
    (s: ShopifyChatSession) => s.session_id === currentShopifySessionId
  );

  // Truncate title for display
  const displayTitle = currentSession?.title
    ? currentSession.title.length > 20
      ? currentSession.title.slice(0, 20) + "..."
      : currentSession.title
    : "New Chat";

  const handleNewChat = async () => {
    selectShopifyChatSession(null); // Clear selection to show empty state
    setIsOpen(false);
  };

  const handleSelectSession = (sessionId: string) => {
    selectShopifyChatSession(sessionId);
    onSessionSelect(sessionId);  // Load messages for the selected session
    setIsOpen(false);
  };

  const handleStartRename = (session: ShopifyChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.session_id);
    setEditTitle(session.title);
  };

  const handleRename = async (sessionId: string) => {
    if (editTitle.trim()) {
      await renameShopifyChatSession(sessionId, editTitle.trim());
    }
    setEditingSessionId(null);
    setEditTitle("");
  };

  const handleDeleteClick = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteSessionId(sessionId);
    deleteModalRef.current?.showModal();
  };

  const handleConfirmDelete = async () => {
    if (deleteSessionId) {
      await deleteShopifyChatSession(deleteSessionId);
      setDeleteSessionId(null);
      deleteModalRef.current?.close();
    }
  };

  return (
    <div className="flex items-center gap-1">
      {/* New Chat Button */}
      <button
        onClick={handleNewChat}
        className="btn btn-ghost btn-sm btn-square"
        title="New Chat"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4v16m8-8H4"
          />
        </svg>
      </button>

      {/* Session Dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="btn btn-ghost btn-sm gap-1 max-w-[160px]"
        >
          <span className="truncate text-sm">{displayTitle}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {isOpen && (
          <ul className="absolute right-0 top-full mt-1 z-50 menu p-2 shadow-lg bg-base-100 rounded-box w-64 max-h-72 overflow-y-auto border border-base-300">
            {shopifyChatSessions.length === 0 ? (
              <li className="text-base-content/50 p-2 text-sm text-center">
                No conversations yet
              </li>
            ) : (
              shopifyChatSessions.map((session: ShopifyChatSession) => (
                <li key={session.session_id}>
                  {editingSessionId === session.session_id ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleRename(session.session_id);
                      }}
                      className="flex gap-1 p-1"
                    >
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="input input-xs input-bordered flex-1"
                        autoFocus
                        onBlur={() => handleRename(session.session_id)}
                      />
                    </form>
                  ) : (
                    <div
                      onClick={() => handleSelectSession(session.session_id)}
                      className={`flex items-center justify-between gap-2 cursor-pointer ${
                        currentShopifySessionId === session.session_id
                          ? "active"
                          : ""
                      }`}
                    >
                      <span className="truncate flex-1 text-sm">
                        {session.title.length > 25
                          ? session.title.slice(0, 25) + "..."
                          : session.title}
                      </span>
                      <div className="flex gap-0.5 shrink-0">
                        {session.is_busy && (
                          <span className="loading loading-spinner loading-xs" />
                        )}
                        <button
                          onClick={(e) => handleStartRename(session, e)}
                          className="btn btn-ghost btn-xs px-1"
                          title="Rename"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(session.session_id, e)}
                          className="btn btn-ghost btn-xs px-1 text-error"
                          title="Delete"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))
            )}
          </ul>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <dialog ref={deleteModalRef} className="modal modal-bottom sm:modal-middle">
        <div className="modal-box">
          <h3 className="font-bold text-lg">Delete conversation?</h3>
          <p className="py-4 text-base-content/70">
            This will permanently delete this conversation and all its messages.
          </p>
          <div className="modal-action">
            <form method="dialog">
              <button className="btn btn-ghost">Cancel</button>
            </form>
            <button onClick={handleConfirmDelete} className="btn btn-error">
              Delete
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>
    </div>
  );
}
